import React from "react";
import { Badge } from "@material-ui/core";
import { Menu, ShoppingCartOutlined } from "@material-ui/icons";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Image } from "react-bootstrap";
import SearchIcon from "../assets/img/search_icon.png";
import { useHistory } from "react-router-dom";
import { mobile } from "../responsive";
import LocationPrompt from "./LocationPrompt";

const Container = styled.div`
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1000;
  background-color: #2d3e50;
  left: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #2d3e50;
  position: relative;
  box-sizing: border-box;

  @media only screen and (max-width: 850px) {
    flex-direction: column;
    align-items: stretch;
    padding: 8px 12px 0 12px;
    gap: 8px;
  }
`;

const Logo = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 40px;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const LocationChip = styled.div`
  display: flex;
  align-items: center;
  margin-right: 14px;
  max-width: 260px;
`;

const MobileLocationRow = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    width: 100%;
    margin-top: 8px;
  }
`;

const MenuItem = styled.div`
  font-size: 14px;
  cursor: pointer;
  margin-left: 25px;
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const MobileTopRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    width: 100%;
  }
`;

const MobileBottomRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    width: 100%;
    padding-bottom: 8px;
  }
`;

const MobileRight = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    margin-right: 16px;
  }
`;

const MobileSearchContainer = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: stretch;
    border: 1px solid lightgray;
    border-radius: 10px;
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid lightgray;
  border-radius: 10px;
  flex: 2;
  padding: 0;
  margin-right: 25px;
  width: 100%;
`;

const Input = styled.input`
  flex-grow: 1;
  padding: 10px;
  padding-left: 32px;
  font-size: 14px;
  border: none;
  border-radius: 10px 0 0 10px;
  box-sizing: border-box;
  background: white url(${SearchIcon}) no-repeat 10px center;
  background-repeat: no-repeat;
  background-position: 10px center;
  background-size: 17px;
  margin: 0;
  &:focus {
    outline: none;
  }
  ${mobile({ flexGrow: 1 })}
`;

const SearchButton = styled.button`
  padding: 10px 15px;
  font-size: 12px;
  border: none;
  background-color: #e5933b;
  color: white;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: -6px 2px 10px rgba(0, 0, 0, 0.55);
  &:focus {
    outline: none;
    background-color: #c37d2f;
  }
  &:hover {
    background-color: #c37d2f;
  }
`;

const ClickableMenu = styled(Menu)`
  font-size: 34px;
  color: white;
  margin-left: 20px;
  opacity: 0.8;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ff6347; // Tomato color when mouse hovers over it
  }

  &:active {
    transform: scale(0.9);
  }

  &:focus {
    outline: 2px solid #ff6347; // Outline with tomato color when it gets focus
  }
`;

const RetailerWholesalerNavbar = ({ setIsCartOpen }) => {
  const currentVendor = useSelector((state) => state.retailerWholesalerCart.currentVendor);
  const cart = useSelector((state) => state.retailerWholesalerCart.carts[currentVendor] || {});
  const cartCount = Object.values(cart).reduce((total, item) => total + item.quantity, 0);
  const history = useHistory();

  const handleCartClick = () => {
    if (setIsCartOpen) {
      setIsCartOpen(true);
    } else {
      history.push(`/shop/${currentVendor}`);
    }
  };

  const goToProfile = () => {
    history.push("/my_profile"); // Change this to your desired route
  };

  return (
    <Container>
      <Wrapper>
        {/* ── Desktop layout ── */}
        <Left>
          <Logo>
            <Link to="/">
              <Image src="/lusuku_logo_main.png" style={{ width: 90, height: 90, marginBottom: 10, marginRight: 8, imageRendering: "-webkit-optimize-contrast" }} />
            </Link>
          </Logo>
        </Left>
        <Center>
          <SearchContainer>
            <Input type="search" id="search" placeholder="Search products, brands and categories..." />
            <SearchButton>SEARCH</SearchButton>
          </SearchContainer>
        </Center>
        <Right>
          <LocationChip>
            <LocationPrompt compact />
          </LocationChip>
          <MenuItem onClick={handleCartClick}>
            <Badge overlap="rectangular" badgeContent={cartCount} color="secondary">
              <ShoppingCartOutlined style={{ color: '#f8f4f4', fontSize: 34, borderRadius: '50%' }} />
            </Badge>
          </MenuItem>
          <ClickableMenu style={{ fontSize: 34, marginRight: 20 }} onClick={goToProfile} />
        </Right>

        {/* ── Mobile layout ── */}
        <MobileTopRow>
          <Logo>
            <Link to="/">
              <Image src="/lusuku_logo_main.png" style={{ width: 52, height: 52, marginRight: 8, imageRendering: "-webkit-optimize-contrast" }} />
            </Link>
          </Logo>
          <MobileRight>
            <MenuItem onClick={handleCartClick}>
              <Badge overlap="rectangular" badgeContent={cartCount} color="secondary">
                <ShoppingCartOutlined style={{ color: '#f8f4f4', fontSize: 22 }} />
              </Badge>
            </MenuItem>
            <ClickableMenu style={{ fontSize: 28, marginRight: 0 }} onClick={goToProfile} />
          </MobileRight>
        </MobileTopRow>
        <MobileBottomRow>
          <MobileSearchContainer>
            <Input type="search" id="search-mobile" placeholder="Search products, brands and categories..." />
            <SearchButton>SEARCH</SearchButton>
          </MobileSearchContainer>
          <MobileLocationRow>
            <LocationPrompt compact />
          </MobileLocationRow>
        </MobileBottomRow>
      </Wrapper>
    </Container>
  );
};

export default RetailerWholesalerNavbar;
